import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { L } from 'harmony-language';
import { useSnackbar } from '../../../components/notifications/use-snackbar';
import { Load } from '../../../types';
import { dateRangeOverlaps } from '../../../utils/date-time-utils';
import { useDraftsKey } from '../../config-hooks';

interface MergeDraftsOptions {
    drafts: Load[];
}

const mergeDrafts = async (options: MergeDraftsOptions) => {
    return (await axios.post(`${API_BASE}/api/orders/merge`, options.drafts)).data;
}

export const useMergeDraftsMutation = () => {
    const snackbar = useSnackbar();
    const queryKey = useDraftsKey();
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: mergeDrafts,
        onMutate: () => {
            const previousCache = queryClient.getQueryData<Load[]>(queryKey);

            return previousCache;
        },
        onError: (_1, _2, context) => {
            if (context) {
                queryClient.setQueryData<Load[]>(queryKey, context);
            }
        },
        onSuccess: (data: Load[]) => {
            const previousDraftIds = data.flatMap((mergedDraft: Load) => mergedDraft.mergedFromOrderIds);
            const previousCache = queryClient.getQueryData<Load[]>(queryKey);

            if (previousCache) {
                const filteredDrafts = previousCache.filter(draft => !previousDraftIds.some(id => id === draft.id)) || [];
                const fromDate = queryKey[3];
                const toDate = queryKey[4];

                const newDrafts: Load[] = [...filteredDrafts]

                for (const mergedDraft of data) {

                    const mergedDraftWithinCacheDateRange = dateRangeOverlaps(mergedDraft.date, fromDate, toDate);
                    
                    newDrafts.push(...(mergedDraftWithinCacheDateRange ? [mergedDraft] : []));
                }

                queryClient.setQueryData(queryKey, newDrafts);
            }

            const numOfDraftsMergedInto = data.reduce((acc, prev) => {
                return acc + (prev.mergedFromOrderIds?.length || 0);
            }, 0)

            snackbar(L.mergeDraftsSuccessMessage(String(numOfDraftsMergedInto), String(data.length)));
        },
        // onSettled: () => {
        //     queryClient.invalidateQueries(queryKey);
        // },
    });

    return mutation;
};
